import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { AssistantContext, AssistantSession } from './types'

export const assistantKeys = {
  all: ['assistant'] as const,
  session: () => [...assistantKeys.all, 'session'] as const,
  context: () => [...assistantKeys.all, 'context'] as const,
}

/**
 * The assistant conversation and its transcript.
 *
 * **No id in the key, because there is no id to put there.** There is exactly one session per
 * user, server-side, so the transcript is the same wherever it is opened from — which is the
 * point of the assistant living in the shell rather than on a page.
 *
 * `enabled` exists so the shell can mount the dock for everyone and still not call the API
 * until somebody opens it. An unauthenticated visitor never fires this.
 */
export function useAssistantSession(enabled = true): UseQueryResult<AssistantSession> {
  return useQuery({
    queryKey: assistantKeys.session(),
    enabled,
    queryFn: () => api.get<AssistantSession>('/assistant'),
  })
}

/**
 * The context assembled for the model — every skill, aspect, consequence and item it is told
 * about the attached character.
 *
 * Opt-in, because it is only wanted when somebody is asking "why did it say that?". It is also
 * the fastest way to spot a sheet that is not what its owner thinks it is. Empty text when no
 * character is attached, so the caller does not have to special-case that state.
 */
export function useAssistantContext(enabled: boolean): UseQueryResult<AssistantContext> {
  return useQuery({
    queryKey: assistantKeys.context(),
    enabled,
    queryFn: () => api.get<AssistantContext>('/assistant/context'),
  })
}

/**
 * Attaches a character to the conversation, or detaches it when passed null.
 *
 * The transcript survives, which is why this is a mutation on the session rather than a switch
 * between sessions: a player reads a rules answer, then attaches Harry and asks "so what would
 * *he* do?" without losing the first answer.
 *
 * The response is the whole session, so this writes the cache rather than invalidating it. The
 * *context* query is invalidated, because what the model is told has genuinely changed.
 */
export function useSetAssistantCharacter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (characterId: number | null) =>
      api.put<AssistantSession>('/assistant/context', { characterId }),
    onSuccess: (session) => {
      queryClient.setQueryData(assistantKeys.session(), session)
      void queryClient.invalidateQueries({ queryKey: assistantKeys.context() })
    },
  })
}

/**
 * Clears the transcript.
 *
 * The session survives, and so does the attached character — clearing is "start this thread
 * again", not "put the assistant back in its box". The response is the emptied session, so this
 * writes the cache rather than invalidating it.
 */
export function useClearAssistant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.delete<AssistantSession>('/assistant/messages'),
    onSuccess: (session) => {
      queryClient.setQueryData(assistantKeys.session(), session)
    },
  })
}
